import {
  BlockRenderer as SharedBlockRenderer,
  type RenderDiagnosticEvent,
  type RenderFallbackContext,
} from '@emporion/block-renderer'
import { emitFrontendDiagnostic, frontendDiagnosticsEvents } from '@emporion/ui'
import type { BlockInstance } from './types'
import { STOREFRONT_RUNTIME_ERROR_KEY, STOREFRONT_RUNTIME_HTML_KEY } from './storefrontRuntime'
import { shopBlockRenderers } from './blockRendererRegistry'
import { useCategoryCarouselEnhancer } from './useCategoryCarouselEnhancer'

type StorefrontPluginLayout = 'contained' | 'full-bleed' | 'raw'

const isStorefrontPluginLayout = (value: unknown): value is StorefrontPluginLayout =>
  value === 'contained' || value === 'full-bleed' || value === 'raw'

const resolveStorefrontPluginLayout = (block: BlockInstance): StorefrontPluginLayout => {
  if (isStorefrontPluginLayout(block.storefrontLayout)) return block.storefrontLayout
  if (isStorefrontPluginLayout(block.props.storefrontLayout)) return block.props.storefrontLayout
  return 'contained'
}

const runtimeWrapperClassName = (block: BlockInstance): string => {
  const layout = resolveStorefrontPluginLayout(block)
  if (layout === 'raw') return 'contents'
  return 'relative left-1/2 w-screen -translate-x-1/2 overflow-hidden'
}

const runtimeContentClassName = (block: BlockInstance): string | undefined => {
  const layout = resolveStorefrontPluginLayout(block)
  if (layout === 'contained') return 'mx-auto w-full max-w-[1740px] px-4 sm:px-6 lg:px-14'
  return undefined
}

const fallbackMessage = ({ block, kind, reason }: RenderFallbackContext): string => {
  if (kind === 'plugin-runtime-disabled') {
    return `Plugin block non disponibile nello storefront (${block.type})`
  }
  if (kind === 'plugin-runtime-unavailable') {
    return `Plugin block runtime non disponibile (${block.type})`
  }
  if (kind === 'missing-renderer') {
    return reason === 'plugin_renderer_missing'
      ? `Plugin block non supportato nello storefront runtime (${block.type})`
      : `Renderer mancante per ${block.type}`
  }
  if (kind === 'renderer-error') {
    return `Errore nel blocco ${block.type}`
  }
  return `Configurazione non valida per ${block.type}`
}

const renderStorefrontFallback = (context: RenderFallbackContext) => (
  <div
    key={context.block.id}
    className={
      context.kind === 'renderer-error' || context.kind === 'invalid-config'
        ? 'rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800'
        : 'rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'
    }
  >
    {fallbackMessage(context)}
    {context.kind === 'renderer-error' && context.error instanceof Error ? `: ${context.error.message}` : null}
  </div>
)

const emitStorefrontDiagnostic = (event: RenderDiagnosticEvent) => {
  if (event.type === 'renderer-error') {
    if (!event.block.pluginId) return
    emitFrontendDiagnostic({
      app: 'shop',
      level: 'error',
      event: frontendDiagnosticsEvents.storefrontRuntime.failed,
      message: 'Storefront plugin runtime renderer failed',
      context: {
        pluginId: event.block.pluginId,
        blockType: event.block.type,
        runtimeMode: event.block.runtimeMode || null,
        reason: 'renderer_throw',
      },
      error: event.error,
    })
    return
  }

  if (!event.block.pluginId) return
  if (event.kind === 'plugin-runtime-disabled' || event.kind === 'plugin-runtime-unavailable') {
    emitFrontendDiagnostic({
      app: 'shop',
      level: 'warn',
      event: frontendDiagnosticsEvents.storefrontRuntime.failed,
      message:
        event.kind === 'plugin-runtime-disabled'
          ? 'Storefront plugin runtime is not enabled for this block'
          : 'Storefront plugin runtime renderer output is missing',
      context: {
        pluginId: event.block.pluginId,
        blockType: event.block.type,
        runtimeMode: event.block.runtimeMode || null,
        reason: event.reason || null,
      },
    })
  }
  emitFrontendDiagnostic({
    app: 'shop',
    level: 'warn',
    event: frontendDiagnosticsEvents.storefrontRuntime.fallback,
    message: 'Storefront plugin runtime fallback rendered',
    context: {
      pluginId: event.block.pluginId,
      blockType: event.block.type,
      runtimeMode: event.block.runtimeMode || null,
      reason: event.reason || null,
    },
  })
}

function BlockRenderer({ layout, className }: { layout: BlockInstance[]; className?: string }) {
  useCategoryCarouselEnhancer(layout)

  return (
    <SharedBlockRenderer
      layout={layout}
      className={className}
      registry={shopBlockRenderers}
      runtimeHtml={{
        htmlPropName: STOREFRONT_RUNTIME_HTML_KEY,
        errorPropName: STOREFRONT_RUNTIME_ERROR_KEY,
        className: runtimeWrapperClassName,
        contentClassName: runtimeContentClassName,
      }}
      renderFallback={renderStorefrontFallback}
      onDiagnostic={emitStorefrontDiagnostic}
    />
  )
}

export default BlockRenderer
